import React from "react";

import { cn } from "../../ui";
import type { ModuleCategory } from "./types";

type CategoryCount = {
  category: ModuleCategory;
  label: string;
  count: number;
};

type Props = {
  selected: ModuleCategory | "all";
  onChange: (next: ModuleCategory | "all") => void;
  total: number;
  categories: CategoryCount[];
};

const CategoryTabs: React.FC<Props> = ({
  selected,
  onChange,
  total,
  categories,
}) => {
  const renderTab = (
    key: ModuleCategory | "all",
    label: string,
    count: number,
  ) => {
    const isActive = selected === key;
    return (
      <button
        type="button"
        key={key}
        onClick={() => onChange(key)}
        aria-pressed={isActive}
        className={cn(
          "group inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[12.5px] transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/25",
          isActive
            ? "border-brand bg-brand text-white"
            : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50",
        )}
      >
        <span
          className={cn(
            "truncate font-medium tracking-tight",
            isActive ? "" : "text-gray-800",
          )}
        >
          {label}
        </span>
        <span
          className={cn(
            "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[10.5px] font-semibold tabular-nums",
            isActive
              ? "bg-white/20 text-white"
              : "bg-gray-100 text-gray-600 group-hover:bg-gray-200",
          )}
        >
          {count}
        </span>
      </button>
    );
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {renderTab("all", "All modules", total)}
      {categories.map(({ category, label, count }) =>
        count > 0 ? renderTab(category, label, count) : null,
      )}
    </div>
  );
};

export default CategoryTabs;
